import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import isNull from 'lodash/isNull';
import { useNode, useEditor, useEditingCond } from '@splashthat/splash-cms-engine';

import { getInstance as getBootstrap } from 'utils/bootstrap';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import {
    BRAND_LIBRARY_SUBHEADLINE_TEXT,
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import {
    HeadlineContent,
    CalendarAttachmentWrapper,
    LogoWrapper,
    LogoItem,
    LogoLink,
    ProviderLogoName,
} from './CalendarAttachment.styles';
import Outlook from './Icons/Outlook';
import Apple from './Icons/Apple';
import Google from './Icons/Google';
import Yahoo from './Icons/Yahoo';

const uploadHashQuery = `&u=${getBootstrap().get('splashPage.uploadHash')}`;
const formBase = getBootstrap().get('splashPage.formBase');

export const getDefaultProviders = (queryParameters) => [
    {
        label: 'Google',
        provider: 'Gmail',
        icon: Google,
        getLink: () => `${formBase}?calendar-Gmail${queryParameters}`,
    },
    {
        label: 'Outlook',
        provider: 'Outlook',
        icon: Outlook,
        getLink: () => `${formBase}?calendar-Outlook${queryParameters}`,
    },
    {
        label: 'Apple',
        provider: 'iCal',
        icon: Apple,
        getLink: () => `${formBase}?calendar-iCal${queryParameters}`,
    },
    {
        label: 'Yahoo',
        provider: 'Yahoo',
        icon: Yahoo,
        getLink: () => `${formBase}?calendar-Yahoo${queryParameters}`,
    },
];

const CalendarAttachment = React.forwardRef(
    (
        {
            meta: { visible },
            descendants: { headline, providerLogoIcons, providerLogoNames },
            hovered,
            selected,
            providers,
            logoWidth,
            logoHeight,
            gap,
            providerLogoDisplay,
        },

        ref,
    ) => {
        const { theme, guest, event } = useTouchpointData();
        const node = useNode();
        const editor = useEditor((state) => ({
            editing: state.editing,
        }));
        const setContent = useCallback(
            (text) =>
                node.actions.setProp((editedNode) => {
                    editedNode.descendants.headline.meta.content = text; // eslint-disable-line no-param-reassign
                    return editedNode;
                }),
            [],
        );

        // This flag that includes checking both colorHover and color properties to know if the custom color editor is
        //  enabled is needed because we nullify both items sequentially when disabling the toggle. If we only check
        //  color, it will still be different from null and will cause a crash. A good solution would be to allow the
        //  CMS Engine to perform several actions at once. This would also save some extra re-renders.
        const hasCustomLogoColor =
            !isNull(providerLogoIcons.styles.color) && !isNull(providerLogoIcons.styles.colorHover);

        const logoColor = theme.colors[providerLogoIcons.styles.color];
        const logoColorHover = theme.colors[providerLogoIcons.styles.colorHover];
        const { guestAccessHash } = guest;
        const guestAccessHashQuery = guestAccessHash ? `&gha=${guestAccessHash}` : '';
        const queryParameters = `${uploadHashQuery}${guestAccessHashQuery}`;
        const sendAnalytics = (provider) => {
            // eslint-disable-next-line no-unused-expressions
            window?.ga?.('splash.send', 'event', 'CalendarAttachment', `Cal${provider}`, `id:${event.id}`);
            // eslint-disable-next-line no-unused-expressions
            window?.gtag?.('event', 'calendar_attachment', {
                event_category: `Cal${provider}`,
            });
        };

        const handleClickProviderLink = (calendarProvider) => {
            const downloadCalendarLink = calendarProvider.getLink();
            const shouldGetCalendarAttachmentDownload = typeof downloadCalendarLink === 'function';
            if (shouldGetCalendarAttachmentDownload) {
                downloadCalendarLink();
            }
            sendAnalytics(calendarProvider.provider);
        };

        const onClickProviderLink = useEditingCond(() => {}, handleClickProviderLink);
        const calendarProviders = providers || getDefaultProviders(queryParameters);

        return visible ? (
            <CalendarAttachmentWrapper
                selected={selected}
                hovered={hovered}
                onMouseLeave={node.actions.onMouseLeave()}
                onMouseMove={node.actions.onMouseMove()}
                onClick={node.actions.onClick()}
                ref={ref}
                gap={gap}
            >
                <HeadlineContent
                    $fontColor={theme.colors[headline.styles.fontColor]}
                    $fontFamily={theme.activeFonts[headline.styles.fontFamily]}
                    $fontSize={headline.styles.fontSize}
                >
                    <TextEditable setContent={setContent} disabled={!editor.editing || (!selected && !hovered)}>
                        {headline.meta.content}
                    </TextEditable>
                </HeadlineContent>
                <LogoWrapper>
                    {calendarProviders.map((calendarProvider) => {
                        const calendarLink = calendarProvider.getLink();
                        const isCalendarLinkHref = typeof calendarLink === 'string';
                        const Icon = calendarProvider.icon;
                        const linkAttributes = isCalendarLinkHref
                            ? {
                                  href: calendarLink,
                                  target: '_blank',
                                  rel: 'nofollow noopener noreferrer',
                                  role: 'link',
                              }
                            : { role: 'button' };

                        return (
                            <LogoItem key={calendarProvider.label}>
                                <LogoLink
                                    onClick={() => onClickProviderLink(calendarProvider)}
                                    $logoColor={logoColor}
                                    $logoColorHover={logoColorHover}
                                    aria-label={calendarProvider.label}
                                    data-qa={`${calendarProvider.label}--Calendar-link`}
                                    {...linkAttributes}
                                >
                                    <Icon
                                        logoColor={hasCustomLogoColor ? 'currentColor' : null}
                                        logoWidth={logoWidth}
                                        logoHeight={logoHeight}
                                        decorative
                                    />
                                    <ProviderLogoName
                                        $fontColor={theme.colors[providerLogoNames?.styles?.fontColor]}
                                        $fontFamily={theme.activeFonts[providerLogoNames?.styles?.fontFamily]}
                                        $fontSize={providerLogoNames?.styles?.fontSize}
                                        providerLogoDisplay={providerLogoDisplay}
                                    >
                                        {calendarProvider.label}
                                    </ProviderLogoName>
                                </LogoLink>
                            </LogoItem>
                        );
                    })}
                </LogoWrapper>
            </CalendarAttachmentWrapper>
        ) : null;
    },
);

CalendarAttachment.splash = {
    related: {
        settings: React.lazy(() => import('./CalendarAttachment.tools')),
    },
    defaultProps: {
        styles: {},
        meta: {
            displayName: 'Calendar Attachment',
            visible: true,
        },
        descendants: {
            headline: {
                styles: {
                    fontSize: 14,
                    fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                    fontFamily: BRAND_LIBRARY_SUBHEADLINE_TEXT,
                },
                meta: {
                    content: 'Add to Calendar',
                },
            },
            providerLogoIcons: {
                styles: {
                    color: null,
                    colorHover: null,
                },
            },
            providerLogoNames: {
                styles: {
                    fontSize: 14,
                    fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                    fontFamily: BRAND_LIBRARY_BODY_TEXT,
                },
            },
        },
    },
};

CalendarAttachment.propTypes = {
    meta: PropTypes.shape({
        content: PropTypes.string,
        visible: PropTypes.bool,
    }).isRequired,
    descendants: PropTypes.object.isRequired,
    hovered: PropTypes.bool.isRequired,
    selected: PropTypes.bool.isRequired,
    providers: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            provider: PropTypes.string,
            icon: PropTypes.elementType,
            getLink: PropTypes.func,
        }),
    ),
    logoWidth: PropTypes.number,
    logoHeight: PropTypes.number,
};

CalendarAttachment.defaultProps = {
    providers: undefined,
    gap: 16,
};

export default CalendarAttachment;
